import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Optional auth token getter. If provided and returns a token, it will be sent as a Bearer token.
 */
export const MATRIX_AUTH_TOKEN_GETTER = new InjectionToken<() => string | null>(
  'MATRIX_AUTH_TOKEN_GETTER',
  {
    factory: () => () => {
      try {
        return localStorage.getItem('authToken');
      } catch {
        return null;
      }
    },
  }
);

export type HttpClientOptions = Readonly<{
  headers?: HttpHeaders | Record<string, string | string[]>;
  params?: HttpParams | Record<string, string | string[]>;
}>;

@Injectable({ providedIn: 'root' })
export class HttpClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.matrixApiUrl;


  private createDefaultHeaders(
    customHeaders?: HttpHeaders | Record<string, string | string[]>
  ): HttpHeaders {
    let headers =
      customHeaders instanceof HttpHeaders ? customHeaders : new HttpHeaders(customHeaders);

    if (!headers.has('Content-Type')) headers = headers.set('Content-Type', 'application/json');

    // Retained from the original service; safe no-op if backend ignores it.
    headers = headers.set('X-No-Proxy', 'true');

    return headers;
  }

  private buildUrl(path: string): string {
    // Absolute URL: return as-is.
    if (/^https?:\/\//i.test(path)) return path;
    if (!this.baseUrl) return path;
    return `${this.baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  get<T>(url: string, options?: HttpClientOptions): Observable<T> {
    const headers = this.createDefaultHeaders(options?.headers);
    return this.http.get<T>(this.buildUrl(url), { headers, params: options?.params });
  }

  post<T>(url: string, body: unknown, options?: HttpClientOptions): Observable<T> {
    const headers = this.createDefaultHeaders(options?.headers);
    return this.http.post<T>(this.buildUrl(url), body, { headers, params: options?.params });
  }

  put<T>(url: string, body: unknown, options?: HttpClientOptions): Observable<T> {
    const headers = this.createDefaultHeaders(options?.headers);
    return this.http.put<T>(this.buildUrl(url), body, { headers, params: options?.params });
  }

  patch<T>(url: string, body: unknown, options?: HttpClientOptions): Observable<T> {
    const headers = this.createDefaultHeaders(options?.headers);
    return this.http.patch<T>(this.buildUrl(url), body, { headers, params: options?.params });
  }

  delete<T>(url: string, options?: HttpClientOptions): Observable<T> {
    const headers = this.createDefaultHeaders(options?.headers);
    return this.http.delete<T>(this.buildUrl(url), { headers, params: options?.params });
  }
}
